import { RingBuffer } from './ring-buffer'

/** Set on every backend response by `backend/src/middleware/requestId.ts`. */
export const REQUEST_ID_HEADER = 'x-request-id'

export interface RequestIdEntry {
  id: string
  method: string
  path: string
  status: number
  ts: number
}

interface Options {
  capacity: number
  now?: () => number
}

interface ObservedResponse {
  headers: Headers
  status: number
  method: string
  path: string
}

export interface RequestIdTrail {
  recordResponse(response: ObservedResponse): void
  snapshot(): RequestIdEntry[]
  droppedCount(): number
}

/**
 * Remembers the correlation ids of recent API calls.
 *
 * The backend already stamps `X-Request-ID` on every response and the frontend
 * has never read it. Capturing the last few turns a bundle from a description
 * of the symptom into something joinable against server logs, OTel traces and
 * Sentry — which is the job AGI's `main.log` tail does for them and which a
 * browser has no equivalent of.
 */
export function createRequestIdTrail(options: Options): RequestIdTrail {
  const { capacity, now = (): number => Date.now() } = options
  const buffer = new RingBuffer<RequestIdEntry>(capacity)

  return {
    recordResponse: (response): void => {
      // Headers.get is case-insensitive per the fetch spec.
      const id = response.headers.get(REQUEST_ID_HEADER)
      // No id means nothing to join on server-side; recording it would pad the
      // trail with entries that cannot be looked up.
      if (id === null || id === '') return

      buffer.push({
        id,
        method: response.method,
        path: response.path,
        status: response.status,
        ts: now(),
      })
    },
    snapshot: (): RequestIdEntry[] => buffer.snapshot(),
    droppedCount: (): number => buffer.droppedCount(),
  }
}
